import { FC, useState, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useStyles } from './styles';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useHistory } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { observer } from 'mobx-react-lite';
import { BasePathInput } from 'components/BasePathInput';
import {
  Paper,
  FormControlLabel,
  Container,
  Grid,
  FormControl,
  Checkbox,
  TextField,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
} from '@material-ui/core';
import { OrganizationFormInputPattern, DepartmentData } from './types';
import { handleCompanyUrl } from 'utils/organizationUtils';
import MuiPhoneNumber from 'material-ui-phone-number-2';
import PromptComponent from 'components/Prompt/Prompt';
import { Autocomplete } from '@mui/material';
import { GoogleProfile } from './types';

export const OrganizationForm: FC<any> = observer(({ type, currentDepartmeent }) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    ldapStore: { updateDepartment, createDepartment, getDepartments, usersView, getUsers },
    adminsTableStore: { getAdmins, adminsView },
    AppStore: { providerList },
    notificationStore: { openNotification },
    baseTreeExplorerStore: { selectedBase },
  } = useStore();
  const [showConnectedButton, setShowConnectedButton] = useState<string | null>(null);
  const [showConnectedStatus, setShowConnectedStatus] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(currentDepartmeent?.reSyncProvider || []);
  const [openPopup, setOpenPopup] = useState(false);
  const [optionToUncheck, setOptionToUncheck] = useState<string | null>(null);
  const [previousSelectedOptions, setPreviousSelectedOptions] = useState<string[]>([]);
  const [isCancelClicked, setIsCancelClicked] = useState(false);
  const [confirm, setConfirm] = useState('no');
  const [removedOption, setRemovedOption] = useState<string[]>([]);
  const [options, setOptions] = useState<string[]>([]);
  const [googleProfile, setGoogleProfile] = useState<GoogleProfile | undefined>(undefined);
  const [selectedManagers, setSelectedManagers] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [adminsData, setAdminsData] = useState<any[]>([]);
  const providerListReq = useQuery('providerListReq', () => providerList(), {
    onError: (error) => openNotification('error', `We are unable to connect ${error}`),
  });
  const providerDisplayMap: { [key: string]: string } = {
    google: 'Google Workspace',
  };
  const usersReq = useQuery('usersReq', () => getUsers(), {
    onSuccess: (data) => {
      const userData = data || usersView || [];
      setUsersData(userData);
    },
    onError: (error: any) => {
      console.error('Users fetch error:', error);
      openNotification('error', error);
    },
  });

  const adminsReq = useQuery('adminsReq', () => getAdmins(), {
    onSuccess: (data) => {
      const adminData = data || adminsView || [];
      setAdminsData(adminData);
    },
    onError: (error: any) => openNotification('error', error),
  });
  useEffect(() => {
    if (providerListReq.data) {
      providerListReq.data.forEach((item: any) => {
        if (item.provider === 'google') {
          setGoogleProfile(item);
        }
        const optionView = providerListReq.data
          .map((item: { provider: string }) => providerDisplayMap[item.provider] || item.provider)
          .filter((provider: any) => provider === 'Google Workspace');
        setOptions(optionView);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerListReq.data]);
  useEffect(() => {
    if (currentDepartmeent?.reSyncProvider) {
      setSelectedOptions(currentDepartmeent.reSyncProvider);
    }
  }, [currentDepartmeent]);
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, option: string) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setSelectedOptions((prevOptions: string[]) => {
        if (Array.isArray(prevOptions)) {
          if (!prevOptions.includes(option)) {
            return [...prevOptions, option];
          }
        }
        return prevOptions;
      });
    } else {
      setPreviousSelectedOptions(selectedOptions);
      setOptionToUncheck(option);
      setOpenPopup(true);
    }
  };
  const handleConfirm = () => {
    if (optionToUncheck) {
      setSelectedOptions((prevOptions: any[]) => prevOptions.filter((item: string) => item !== optionToUncheck));
      setRemovedOption((prev) => [...prev, optionToUncheck]);
      setOptionToUncheck(null);
      setConfirm('yes');
    }
    setOpenPopup(false);
  };
  const handleCancel = () => {
    setSelectedOptions(previousSelectedOptions);
    setOptionToUncheck(null);
    setOpenPopup(false);
    setIsCancelClicked(true);
  };
  const updateDepartmentReq = useMutation((data: DepartmentData) => updateDepartment(data), {
    mutationKey: 'updateDepartmentReq',
    onSuccess: async () => {
      await getDepartments();
      openNotification('success', 'Saved!!');
      history.push(handleCompanyUrl('/departments'));
    },
    onError: (error) => {
      openNotification('error', `${error}`);
    },
  });

  const createDepartmentReq = useMutation((data: DepartmentData) => createDepartment(data), {
    mutationKey: 'createDepartmentReq',
    onSuccess: async () => {
      await getDepartments();
      openNotification('success', 'Created Successfully');
      history.push(handleCompanyUrl('/departments'));
    },
    onError: (error) => {
      openNotification('error', `${error}`);
    },
  });

  useEffect(() => {
    setShowConnectedButton(googleProfile?.token || null);
    setShowConnectedStatus(googleProfile?.tokenStatus || null);
  }, [googleProfile]);
  useAppPreloader(updateDepartmentReq.isLoading || createDepartmentReq.isLoading || providerListReq.isFetching);
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<DepartmentData>();

  // Effect to match and select managers from the provided list
  useEffect(() => {
    if (currentDepartmeent?.managers && (usersData.length > 0 || adminsData.length > 0)) {
      const allUsers = [...usersData, ...adminsData];
      const matchedManagers = currentDepartmeent.managers
        .map((manager: any) => {
          // Find matching user by user_id, firstName+secondName, or email
          return allUsers.find(
            (user) =>
              user.user_id === manager.user_id ||
              (user.cn && user.cn.toLowerCase().includes(manager.firstName?.toLowerCase())) ||
              user.mail === manager.email ||
              user.email === manager.email
          );
        })
        .filter(Boolean); // Remove null/undefined matches

      if (matchedManagers.length > 0) {
        setSelectedManagers(matchedManagers);
        setValue('manager_id', matchedManagers);
      }
    }
  }, [usersData, adminsData, currentDepartmeent?.managers, setValue]);
  const appStatusChecked = watch('appStatus');
  const onSubmit = (data: DepartmentData) => {
    // Convert manager objects to user IDs before submission
    if (data.manager_id && Array.isArray(data.manager_id)) {
      data.manager_id = data.manager_id.map((manager: any) => {
        if (typeof manager === 'object') {
          return manager.user_id || null; // Send user_id or null if not available
        }
        return manager; // If it's already a string, keep it
      });
    }

    data.reSyncProvider = appStatusChecked === false ? [] : selectedOptions;
    data.deleteProviderType = appStatusChecked === false ? 'yes' : isCancelClicked ? 'no' : confirm;
    data.deleteProvider =
      appStatusChecked === false
        ? selectedOptions.length === 0
          ? removedOption
          : selectedOptions
        : isCancelClicked
        ? []
        : removedOption;
    (!!currentDepartmeent as boolean) && (data.orgId = currentDepartmeent.orgId);
    (!!currentDepartmeent as boolean) ? updateDepartmentReq.mutate(data) : createDepartmentReq.mutate(data);
  };

  const mockDataProperties: OrganizationFormInputPattern[] = [
    {
      label: 'Department Name',
      key: 'ou',
      autoFocus: true,
      required: true,
      pattern: {
        regexp: /^[0-9A-Za-z_\s@#$%&*()\-+=!.,;:'"/\\[\]{}|<>?^]+$/,
        errorMessage: 'This field must contain only alphanumeric characters, spaces, underscores, or special symbols',
      },
      placeholder: 'Enter Department Name',
    },
    { label: 'Description', key: 'description', required: true, placeholder: 'Enter Department Description' },
    { label: 'Dn', key: 'dn', required: false, disable: true },
    { label: 'Active Till', key: 'end_date', required: false, type: 'date' },
  ];

  const mockDataLocation: OrganizationFormInputPattern[] = [
    { label: 'Site', key: 'st', placeholder: 'Main Office ' },
    { label: 'Location', key: 'l', placeholder: 'New York, NY' },
    { label: 'Address', key: 'postaladdress', placeholder: '1234 Main St, Springfield, IL 62704' },
    { label: 'Phone', key: 'telephonenumber', placeholder: '123-456-789' },
    { label: 'Manager', key: 'manager_id', required: false, type: 'multiselect' },
  ];

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.box}>
        <Grid container justify='space-between'>
          <PromptComponent />
          <Paper className={classes.paper}>
            <p className={classes.boxTitle}>Department Properties</p>
            <Container className={classes.tableBox}>
              <Controller
                name='base'
                render={({ field }) => (
                  <Grid container className={classes.row} style={{ display: 'none' }}>
                    <BasePathInput disabled={type === 'view'} {...field} />
                  </Grid>
                )}
                defaultValue={selectedBase}
                control={control}
              />
              {mockDataProperties.map((item, index) => (
                <Controller
                  key={item.key}
                  name={item.key}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <Grid key={index} container className={item?.multiline ? classes.rowWide : classes.row}>
                      <p className={classes.title}>{item.label}</p>
                      {item.type === 'date' ? (
                        <TextField
                          {...field}
                          type='date'
                          error={invalid}
                          disabled={type === 'view' || item.disable}
                          helperText={invalid ? 'Required field' : ''}
                          className={classes.input}
                          InputLabelProps={{ shrink: true }}
                        />
                      ) : (
                        <TextField
                          {...field}
                          autoFocus={item?.autoFocus}
                          error={invalid}
                          disabled={type === 'view' || item.disable}
                          multiline={item?.multiline}
                          rows={item?.multiline ? 4 : 1}
                          placeholder={item.placeholder}
                          helperText={
                            invalid ? (error?.type === 'pattern' ? item.pattern?.errorMessage : 'Required field') : ''
                          }
                          className={classes.input}
                        />
                      )}
                    </Grid>
                  )}
                  defaultValue={item.key ? currentDepartmeent?.[item.key] : undefined}
                  control={control}
                  rules={{ required: item?.required, pattern: item.pattern?.regexp }}
                />
              ))}
            </Container>
          </Paper>
          <Paper className={classes.paper}>
            <p className={classes.boxTitle}>Location</p>
            <Container className={classes.tableBox}>
              {mockDataLocation.map((item, index) =>
                item.key === 'telephonenumber' ? (
                  <Controller
                    key={item.key}
                    name={item.key}
                    render={({ field, fieldState: { invalid } }) => (
                      <Grid key={index} container className={classes.rows}>
                        <p className={classes.titles}>{item.label}</p>
                        <MuiPhoneNumber
                          {...field}
                          disabled={type === 'view'}
                          autoFocus={item?.autoFocus}
                          multiline={item?.multiline}
                          rows={item?.multiline ? 4 : 1}
                          placeholder={item.placeholder}
                          error={invalid}
                          helperText={invalid ? 'Required field' : ''}
                          data-cy='user-phone'
                          defaultCountry={'us'}
                          variant='standard'
                          color='warning'
                          className={classes.input}
                        />
                      </Grid>
                    )}
                    defaultValue={item.key ? currentDepartmeent?.[item.key] : undefined}
                    control={control}
                    rules={{ required: item?.required }}
                  />
                ) : item.type === 'multiselect' ? (
                  <Controller
                    key={item.key}
                    name={item.key}
                    render={({ field, fieldState: { invalid } }) => (
                      <Grid key={index} container className={classes.rows}>
                        <p className={classes.titles}>{item.label}</p>
                        <FormControl fullWidth>
                          <Autocomplete
                            multiple
                            options={[...usersData, ...adminsData]}
                            value={Array.isArray(field.value) ? field.value : selectedManagers}
                            disablePortal
                            onChange={(event: any, newValue: any[]) => {
                              // Convert Proxy objects to plain objects
                              const plainObjects = newValue.map((item: any) => {
                                if (item && typeof item === 'object') {
                                  return {
                                    dn: item.dn,
                                    uid: item.uid,
                                    cn: item.cn,
                                    sn: item.sn,
                                    mail: item.mail,
                                    base: item.base,
                                    user_id: item.user_id,
                                  };
                                }
                                return item;
                              });
                              field.onChange(plainObjects);
                              // Update selectedManagers state to keep it in sync
                              setSelectedManagers(plainObjects);
                            }}
                            disabled={type === 'view'}
                            getOptionLabel={(option: any) => {
                              return option?.cn || option?.uid || 'Unknown User';
                            }}
                            isOptionEqualToValue={(option: any, value: any) => {
                              if (!option || !value) return false;
                              return option.dn === value.dn || option.uid === value.uid || option.mail === value.mail;
                            }}
                            loading={usersReq.isLoading || adminsReq.isLoading}
                            noOptionsText='No users found'
                            renderOption={(props: any, option: any) => {
                              const selectedValues = Array.isArray(field.value) ? field.value : selectedManagers;

                              const isSelected = selectedValues.some((selected: any) => {
                                // Handle both object and primitive comparisons
                                if (typeof selected === 'object' && selected !== null) {
                                  return (
                                    (selected.dn && selected.dn === option.dn) ||
                                    (selected.uid && selected.uid === option.uid) ||
                                    (selected.mail && selected.mail === option.mail) ||
                                    (selected.user_id && selected.user_id === option.user_id)
                                  );
                                }
                                // Handle primitive values (dn, uid, or email)
                                return selected === option.dn || selected === option.uid || selected === option.mail;
                              });

                              const displayName = option?.cn || option?.uid || 'Unknown User';

                              return (
                                <li {...props}>
                                  <Checkbox checked={isSelected} />
                                  <ListItemText primary={displayName} />
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField {...params} variant='outlined' disabled={type === 'view'} />
                            )}
                          />
                        </FormControl>
                      </Grid>
                    )}
                    defaultValue={item.key === 'manager_id' ? selectedManagers : currentDepartmeent?.[item.key]}
                    control={control}
                    rules={{ required: item?.required }}
                  />
                ) : (
                  <Controller
                    key={item.key}
                    name={item.key}
                    render={({ field, fieldState: { invalid } }) => (
                      <Grid key={index} container className={classes.rows}>
                        <p className={classes.titles}>{item.label}</p>
                        <TextField
                          {...field}
                          autoFocus={item?.autoFocus}
                          error={invalid}
                          disabled={type === 'view'}
                          placeholder={item.placeholder}
                          helperText={invalid ? 'Required field' : ''}
                          className={classes.input}
                        />
                      </Grid>
                    )}
                    defaultValue={item.key ? currentDepartmeent?.[item.key] : undefined}
                    control={control}
                    rules={{ required: item?.required }}
                  />
                )
              )}
            </Container>
          </Paper>
          {type !== 'view' && showConnectedButton && showConnectedStatus === 'active' && (
            <Paper className={classes.paper}>
              <div style={{ marginBottom: '20px' }}>
                <FormControlLabel
                  className={classes.notify}
                  control={
                    <Controller
                      name='appStatus'
                      defaultValue={googleProfile?.is_sync ? true : false}
                      key={'appStatus'}
                      control={control}
                      render={({ field, fieldState: { invalid, error } }) => (
                        <Checkbox
                          disabled={type === 'view'}
                          {...field}
                          defaultChecked={googleProfile?.is_sync ? true : false}
                          onChange={field.onChange}
                          name='appStatus'
                        />
                      )}
                    />
                  }
                  label='Assign Connected Apps to Department'
                />
              </div>
              <Container className={classes.tableBox}>
                <p className={classes.boxTitle}>Select Connected Apps</p>
                <Grid className={classes.row} container justify='space-between'>
                  <FormControl fullWidth>
                    <Controller
                      name='reSyncProvider'
                      control={control}
                      defaultValue={selectedOptions}
                      rules={{
                        required: appStatusChecked && selectedOptions.length === 0,
                      }}
                      render={({ field }) => (
                        <>
                          <Autocomplete
                            multiple
                            disablePortal
                            value={selectedOptions.map((option) => providerDisplayMap[option] || option)}
                            onChange={(event, newValue) => {
                              const selectedProviderNames = newValue.map((displayName) => {
                                return (
                                  Object.keys(providerDisplayMap).find(
                                    (key) => providerDisplayMap[key] === displayName
                                  ) || displayName
                                );
                              });
                              const lastSelectedOption = selectedProviderNames.find(
                                (option) => !selectedOptions.includes(option)
                              );
                              const lastRemovedOption = selectedOptions.find(
                                (option) => !selectedProviderNames.includes(option)
                              );
                              if (lastSelectedOption) {
                                handleCheckboxChange(
                                  { target: { checked: true } } as React.ChangeEvent<HTMLInputElement>,
                                  lastSelectedOption
                                );
                              }
                              if (lastRemovedOption) {
                                handleCheckboxChange(
                                  { target: { checked: false } } as React.ChangeEvent<HTMLInputElement>,
                                  lastRemovedOption
                                );
                              }
                              setSelectedOptions(selectedProviderNames);
                              field.onChange(newValue);
                            }}
                            disabled={type === 'view' || appStatusChecked === false}
                            renderOption={(props: any, option: any) => {
                              const providerName =
                                Object.keys(providerDisplayMap).find((key) => providerDisplayMap[key] === option) ||
                                option;
                              return (
                                <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                                  <Checkbox
                                    checked={selectedOptions.includes(providerName)}
                                    onChange={(event) => handleCheckboxChange(event, providerName)}
                                    style={{ marginRight: 8 }}
                                  />
                                  <ListItemText primary={option} />
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant='outlined'
                                error={!!errors.reSyncProvider && appStatusChecked && selectedOptions.length === 0}
                                helperText={
                                  !!errors.reSyncProvider && appStatusChecked && selectedOptions.length === 0
                                    ? 'Please select at least one option'
                                    : ''
                                }
                                disabled={type === 'view'}
                              />
                            )}
                            options={options}
                          />
                        </>
                      )}
                    />
                  </FormControl>
                </Grid>
                <Dialog
                  open={openPopup}
                  onClose={handleCancel}
                  aria-labelledby='confirm-dialog-title'
                  aria-describedby='confirm-dialog-description'
                >
                  <DialogTitle>
                    As you have unselected synchronization, do you want to delete this option from all unchecked
                    third-party apps?
                  </DialogTitle>
                  <DialogActions>
                    <Button onClick={handleCancel} color='primary'>
                      Cancel
                    </Button>
                    <Button onClick={handleConfirm} color='primary'>
                      Confirm
                    </Button>
                  </DialogActions>
                </Dialog>
              </Container>
            </Paper>
          )}
        </Grid>
        {type === 'view' ? (
          ' '
        ) : (
          <div className={classes.btnBox}>
            <Button
              type='button'
              variant='contained'
              color='primary'
              className={classes.btnWideLeft}
              onClick={() => history.push(handleCompanyUrl('/departments'))}
            >
              Cancel
            </Button>

            <Button type='submit' variant='contained' color='secondary' className={classes.btnWide}>
              {(!!currentDepartmeent as boolean) ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        )}
      </form>
    </>
  );
});
